import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Loader2, Save, Lock, Eye, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { requestTypeSettingsApi, requestsApi } from '../../lib/api';
import { getRoleLabel } from '../../lib/utils';

const CAP_LABELS = {
  request_access: {
    title: 'Request access',
    hint: 'This role can see and open this request type at all (list + page). Off = locked out unless they\u2019re a signatory or a configured review-stage reviewer.',
  },
  view_all_signatures: {
    title: 'All e-signatures',
    hint: 'See every document\u2019s signatures (otherwise only documents they are involved with)',
  },
  view_all_discussions: {
    title: 'All discussions',
    hint: 'See every discussion thread (otherwise invitation-only)',
  },
  signatory_access: {
    title: 'Signatory access',
    hint: 'Being a signatory grants access to the request (list + page)',
  },
  stage_reviewer_full_view: {
    title: 'Stage-reviewer full view',
    hint: 'Configured review-stage reviewers see the full signature panel',
  },
};

const TAB_LABELS = {
  overview: 'Overview',
  assignments: 'Assignments',
  documents: 'Documents',
  discussion: 'Discussion',
  approvals: 'Approvals',
  signatures: 'Signatures',
  response: 'Response',
  audit_log: 'Audit Log',
};

// Phase A: Role chips per request type + per-tab on/off toggles + live preview pane.
// COMPANY_REP stays locked (privacy by design).
export function RoleVisibilityConfig({ requestType }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [caps, setCaps] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [configurableRoles, setConfigurableRoles] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [openRole, setOpenRole] = useState(null); // role expanded in the editor pane

  // Preview state
  const [previewRole, setPreviewRole] = useState('INVESTIGATOR');
  const [previewSampleId, setPreviewSampleId] = useState('');
  const [previewSamples, setPreviewSamples] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await requestTypeSettingsApi.getRoleVisibility(requestType);
      setCaps(res.data.capabilities || []);
      setTabs(res.data.tabs || []);
      setConfigurableRoles(res.data.configurable_roles || []);
      setMatrix(res.data.roles || {});
    } catch {
      toast.error('Failed to load role visibility configuration');
    } finally {
      setLoading(false);
    }
  }, [requestType]);

  useEffect(() => { load(); }, [load]);

  // Load up to 20 recent requests of this type for the preview sample dropdown.
  const loadPreviewSamples = useCallback(async () => {
    try {
      const res = await requestsApi.getAll({ type: requestType });
      const items = (res.data || []).slice(0, 20).map(r => ({
        id: r.id, label: `${r.id} — ${r.title || ''}`,
      }));
      setPreviewSamples(items);
      if (items.length) {
        setPreviewSampleId(prev => prev || items[0].id);
      }
    } catch {
      // ignore
    }
  }, [requestType]);

  useEffect(() => { loadPreviewSamples(); }, [loadPreviewSamples]);

  const refreshPreview = useCallback(async () => {
    if (!previewRole) return;
    setPreviewLoading(true);
    try {
      const params = { role: previewRole };
      if (previewSampleId) params.request_id = previewSampleId;
      const res = await requestTypeSettingsApi.previewRoleVisibility(requestType, params);
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [requestType, previewRole, previewSampleId]);

  useEffect(() => { refreshPreview(); }, [refreshPreview]);  const toggleCap = (role, cap) => {
    setMatrix(prev => ({
      ...prev,
      [role]: { ...prev[role], [cap]: !prev[role]?.[cap] },
    }));
  };

  const toggleTab = (role, tab) => {
    setMatrix(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        tabs: { ...(prev[role]?.tabs || {}), [tab]: !(prev[role]?.tabs?.[tab]) },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      configurableRoles.forEach(r => {
        const row = matrix[r] || {};
        const clean = {};
        caps.forEach(c => { clean[c] = !!row[c]; });
        clean.tabs = {};
        tabs.forEach(t => { clean.tabs[t] = !!row.tabs?.[t]; });
        payload[r] = clean;
      });
      await requestTypeSettingsApi.updateRoleVisibility(requestType, payload);
      toast.success('Role visibility configuration saved');
      refreshPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const accessChips = useMemo(() => {
    return configurableRoles.map(r => ({
      role: r,
      label: getRoleLabel(r, requestType),
      enabled: !!matrix[r]?.request_access,
    }));
  }, [configurableRoles, matrix, requestType]);

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 py-4" data-testid="role-visibility-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-5" data-testid={`role-visibility-config-${requestType}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-2xl">
          Control what each role can see inside <b>{requestType}</b> requests. Signatories
          and configured review-stage reviewers always keep access to what they must act on;
          these toggles control the wider visibility.
        </p>
        <Button onClick={handleSave} disabled={saving} className="bg-blue-900 hover:bg-blue-950" data-testid="role-visibility-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
      </div>

      {/* ─── Step 1: Request access chips ─────────────────────────────────────── */}
      <section data-testid="role-visibility-access-chips">
        <div className="flex items-center justify-between mb-1.5">
          <div>
            <div className="text-sm font-semibold text-slate-800">Who can see {requestType} requests</div>
            <div className="text-[11px] text-slate-500">Toggle a chip OFF to hide the entire request type from that role (replaces the legacy per-user &ldquo;Visible Requests&rdquo; list).</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {accessChips.map(({ role, label, enabled }) => (
            <button
              key={role}
              type="button"
              onClick={() => toggleCap(role, 'request_access')}
              data-testid={`role-access-chip-${role}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                enabled
                  ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                  : 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100 line-through'
              }`}
            >
              {label}
            </button>
          ))}
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-slate-200 bg-slate-50 text-slate-500" data-testid="role-access-chip-COMPANY_REP">
            <Lock className="w-3 h-3" /> {getRoleLabel('COMPANY_REP', requestType)} — locked
          </span>
        </div>
      </section>

      {/* ─── Step 2: Per-role capability + tab matrix ─────────────────────────── */}
      <section data-testid="role-visibility-matrix">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-sm font-semibold text-slate-800">Per-role configuration</div>
          <div className="text-[11px] text-slate-500">Click a role to expand its capabilities and tab visibility.</div>
        </div>
        <div className="space-y-2">
          {[...configurableRoles, 'COMPANY_REP'].map(role => {
            const locked = role === 'COMPANY_REP';
            const row = matrix[role] || {};
            const isOpen = openRole === role;
            const enabledTabCount = tabs.filter(t => row.tabs?.[t]).length;
            return (
              <div
                key={role}
                className={`rounded-lg border ${locked ? 'border-slate-200 bg-slate-50/70' : 'border-slate-200 bg-white'}`}
                data-testid={`role-visibility-row-${role}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenRole(isOpen ? null : role)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left"
                  data-testid={`role-visibility-expand-${role}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800 text-sm">{getRoleLabel(role, requestType)}</span>
                    {locked && (
                      <Badge variant="outline" className="text-[10px] gap-1 text-slate-500"><Lock className="w-3 h-3" /> Locked</Badge>
                    )}
                    {!locked && !row.request_access && (
                      <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">No request access</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-slate-500">
                    <span>Tabs: <b className="text-slate-700">{enabledTabCount}/{tabs.length}</b></span>
                    <span className="text-slate-300">·</span>
                    <span>{isOpen ? 'Collapse' : 'Expand'}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 space-y-3 border-t border-slate-100 pt-3">
                    {/* Capabilities */}
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Capabilities</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {caps.map(cap => (
                          <label
                            key={cap}
                            className={`flex items-start gap-2 p-2 rounded border ${locked ? 'opacity-60' : 'border-slate-200'}`}
                          >
                            <Switch
                              checked={!!row[cap]}
                              onCheckedChange={() => !locked && toggleCap(role, cap)}
                              disabled={locked}
                              data-testid={`role-visibility-switch-${role}-${cap}`}
                            />
                            <div className="text-xs leading-snug">
                              <div className="font-medium text-slate-700">{CAP_LABELS[cap]?.title || cap}</div>
                              <div className="text-slate-400">{CAP_LABELS[cap]?.hint}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Tabs */}
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Tabs visible to this role</div>
                      <div className="flex flex-wrap gap-2">
                        {tabs.map(t => {
                          const on = !!row.tabs?.[t];
                          return (
                            <button
                              key={t}
                              type="button"
                              onClick={() => !locked && toggleTab(role, t)}
                              disabled={locked}
                              data-testid={`role-visibility-tab-${role}-${t}`}
                              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                                on
                                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                  : 'bg-slate-50 border-slate-200 text-slate-400 line-through'
                              } ${locked ? 'opacity-60 cursor-not-allowed' : 'hover:brightness-105'}`}
                            >
                              {TAB_LABELS[t] || t}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1.5">Phase B will add granular per-tab content controls (upload, mention, undo, etc.).</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── Step 3: Live preview ────────────────────────────────────────────── */}
      <section className="rounded-lg border border-blue-200 bg-blue-50/30 p-3" data-testid="role-visibility-preview">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="w-4 h-4 text-blue-700" />
          <div className="text-sm font-semibold text-blue-900">What this role would see right now</div>
        </div>
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-slate-500">Role</label>
            <select
              value={previewRole}
              onChange={(e) => setPreviewRole(e.target.value)}
              className="mt-0.5 px-2 py-1 text-xs border border-slate-300 rounded bg-white"
              data-testid="role-visibility-preview-role"
            >
              {[...configurableRoles, 'COMPANY_REP'].map(r => (
                <option key={r} value={r}>{getRoleLabel(r, requestType)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-slate-500">Sample request</label>
            <select
              value={previewSampleId}
              onChange={(e) => setPreviewSampleId(e.target.value)}
              className="mt-0.5 px-2 py-1 text-xs border border-slate-300 rounded bg-white min-w-[260px]"
              data-testid="role-visibility-preview-sample"
            >
              <option value="">— No sample (capability-only) —</option>
              {previewSamples.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refreshPreview}
            disabled={previewLoading}
            data-testid="role-visibility-preview-refresh"
          >
            {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Refresh'}
          </Button>
        </div>

        {!preview && !previewLoading && (
          <p className="text-xs text-slate-500">Pick a role to see what they would experience.</p>
        )}

        {preview && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Visible tabs</div>
              <div className="flex flex-wrap gap-1.5">
                {tabs.map(t => {
                  const on = !!preview.tabs?.[t];
                  return (
                    <span key={t} className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-100 border-slate-200 text-slate-400 line-through'}`}
                          data-testid={`role-visibility-preview-tab-${t}`}>
                      {TAB_LABELS[t] || t}
                    </span>
                  );
                })}
              </div>
              {preview.reasons?.length > 0 && (
                <ul className="mt-2 space-y-1 text-[11px] text-amber-700">
                  {preview.reasons.map((r, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Sample-request impact</div>
              {preview.counts ? (
                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    {preview.counts.can_open_request
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                    <span>{preview.counts.can_open_request ? 'Can open the request' : 'Cannot open the request (no access path)'}</span>
                  </div>
                  <div className="text-slate-600">
                    <span className="font-semibold text-slate-800">{preview.counts.doc_threads_visible}</span>
                    {' of '}<span className="text-slate-500">{preview.counts.doc_threads_total}</span> document discussion threads
                  </div>
                  <div className="text-slate-600">
                    <span className="font-semibold text-slate-800">{preview.counts.signatures_visible}</span>
                    {' of '}<span className="text-slate-500">{preview.counts.signatures_total}</span> signature rows
                  </div>
                  {preview.sample_user?.name && (
                    <div className="text-[11px] text-slate-500 pt-1 border-t mt-1.5">
                      Sample user: <b>{preview.sample_user.name}</b>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[11px] text-slate-500">Pick a sample request above to see concrete counts (threads visible, signatures visible, can-open).</p>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default RoleVisibilityConfig;
